import { useEffect, useMemo } from 'react'
import {
  experimental_useDatePickerState as useDatePickerState,
  useFilterState,
  useSafeLayoutEffect,
} from '@vtex/admin-ui'
import { useLocalStorage } from '@/hooks/useLocalStorage'

import {
  dateObjectToPickerValue,
  handleHide,
  isoStringToPickerValue,
  pickerValueToIsoString,
  RANGE_BUILDERS,
} from './helpers'

export function useFilterCreatedAtState(key: string) {
  const today = new Date()

  const pureFilterState = useFilterState<any>()
  const pureStartDate = useDatePickerState()
  const pureEndDate = useDatePickerState()

  const [storedValue, setStoredValue] = useLocalStorage<{
    appliedItem?: typeof pureFilterState.appliedItem
    startDate?: string
    endDate?: string
  }>(key, {
    appliedItem: pureFilterState.appliedItem,
    startDate: undefined,
    endDate: undefined,
  })

  useSafeLayoutEffect(() => {
    if (
      !storedValue?.appliedItem ||
      !storedValue?.endDate ||
      !storedValue?.startDate
    )
      return
    pureFilterState.setAppliedItem(storedValue.appliedItem)
    pureStartDate.selectDate(
      isoStringToPickerValue(storedValue.startDate ?? '') as any
    )
    pureEndDate.selectDate(
      isoStringToPickerValue(storedValue.endDate ?? '') as any
    )
  }, [])

  useEffect(() => {
    const { id = '' } = pureFilterState.combobox.selectedItem ?? {}

    if (id in RANGE_BUILDERS) {
      const [newStartDate, newEndDate] = RANGE_BUILDERS[id](today)

      pureStartDate.selectDate(dateObjectToPickerValue(newStartDate))

      pureEndDate.selectDate(dateObjectToPickerValue(newEndDate))
    }
  }, [pureFilterState.combobox.selectedItem?.id])

  const state = useMemo(() => {
    const shouldBypassHideFunction =
      pureFilterState.combobox.selectedItem?.id === 'custom' &&
      (pureStartDate.pickerState.visible || pureEndDate.pickerState.visible)

    const isOutOfBounds =
      pureEndDate.calendarState.dateValue <
      pureStartDate.calendarState.dateValue

    const startDate: typeof pureStartDate = {
      ...pureStartDate,
      calendarState: {
        ...pureStartDate.calendarState,
        maxDateValue: pureEndDate.calendarState.dateValue,
        isInvalidDateRange: (date) =>
          date > pureEndDate.calendarState.dateValue,
      },
      tone: isOutOfBounds ? 'critical' : 'neutral',
    }

    const endDate: typeof pureEndDate = {
      ...pureEndDate,
      calendarState: {
        ...pureEndDate.calendarState,
        minDateValue: pureStartDate.calendarState.dateValue,
        isInvalidDateRange: (date) =>
          date < pureStartDate.calendarState.dateValue,
      },
      tone: isOutOfBounds ? 'critical' : 'neutral',
    }

    const filterState = {
      ...pureFilterState,
      menu: {
        ...pureFilterState.menu,
        hide: handleHide(pureFilterState.menu.hide, !shouldBypassHideFunction),
      },
      combobox: {
        ...pureFilterState.combobox,
        hide: handleHide(
          pureFilterState.combobox.hide,
          !shouldBypassHideFunction
        ),
      },
      onChange: () => {
        pureFilterState.onChange()
        setStoredValue({
          appliedItem: filterState.combobox.selectedItem,
          startDate: pickerValueToIsoString({
            ...pureStartDate.dateValue,
            hours: 0,
            minutes: 0,
            seconds: 0,
          }),
          endDate: pickerValueToIsoString({
            ...pureEndDate.dateValue,
            hours: 23,
            minutes: 59,
            seconds: 59,
          }),
        })
      },
      onClear: () => {
        pureFilterState.onClear()
        pureStartDate.selectDate(dateObjectToPickerValue(today))
        pureEndDate.selectDate(dateObjectToPickerValue(today))
        setStoredValue({
          appliedItem: undefined,
          startDate: undefined,
          endDate: undefined,
        })
      },
    }

    return {
      ...filterState,
      appliedItem: filterState.combobox.selectedItem,
      pickerState: {
        startDate,
        endDate,
      },
      appliedDates: {
        startDate: storedValue.startDate,
        endDate: storedValue.endDate,
      },
    }
  }, [pureFilterState, pureStartDate, pureEndDate, storedValue])

  return state
}
